import clsx from "clsx";
import styles from "./Row.module.scss";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";
export type AvatarShape = "circle" | "rounded";

export interface AvatarProps {
  /** Pre-computed initials — callers own the derivation (person vs band vs org rules differ). */
  initials: string;
  /** A photo/logo, when there is one; it covers the box and the initials drop. */
  src?: string | null;
  /** `xs` 24px · `sm` 28px · `md` 40px (default) · `lg` 54px · `xl` 64px. `sm` is the stacked-roster size —
   * an avatar read as a GROUP (the opportunity invitee row), not as one person's identity; `xs` is the
   * shell chrome rung (the account chip and its menu rows), where the mark rides inside a 34px pill. */
  size?: AvatarSize;
  /** People are circles (`.av`); orgs and bands are rounded squares. */
  shape?: AvatarShape;
  className?: string;
}

/**
 * The initials avatar — the spec's `.av` / `.av--lg`: a flat fill with mono initials. Flat because the
 * guide bans gradients (the shipped sheet still gradients these; the guide wins — see
 * STYLE-GUIDE #11).
 */
export function Avatar({ initials, src, size = "md", shape = "circle", className }: AvatarProps) {
  if (src) {
    return (
      <img
        className={clsx(styles.avatar, styles.avatarImg, className)}
        data-size={size}
        data-shape={shape}
        src={src}
        alt=""
      />
    );
  }
  return (
    <span className={clsx(styles.avatar, className)} data-size={size} data-shape={shape} aria-hidden="true">
      {/* Its own box, so it can be trimmed to cap height and centred on the LETTERS (Row.module.scss
          `.avatarInitials`) — bare text in the grid is centred on the font's box, which sits the caps high. */}
      <span className={styles.avatarInitials}>{initials}</span>
    </span>
  );
}
